import React, { useState, useEffect, useMemo } from 'react';
import api from './api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// --- Import required components and styles ---
import AddSim from './AddSim';
import PdfUpload from './PdfUpload';
import AllReportsButton from './AllReportsButton';
import '../styles/SimList.css';
import '../styles/Table.css';
import '../styles/form.css';
import '../styles/modal.css';

const SimList = () => {
    // --- STATE MANAGEMENT ---
    const [sims, setSims] = useState([]);
    const [expandedSimId, setExpandedSimId] = useState(null);
    const [usageHistory, setUsageHistory] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'latest_usage_date', direction: 'desc' });
    const [historyView, setHistoryView] = useState('column');
    const [selectedSims, setSelectedSims] = useState([]);
    const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
    const [selectedSim, setSelectedSim] = useState(null);
    const [assignmentData, setAssignmentData] = useState({
        organisation: '', device_serial: '', device_user: '', user_email: ''
    });
    const [quickSearchTerm, setQuickSearchTerm] = useState('');
    const [filters, setFilters] = useState({ organisation: '', assignedTo: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [isReportLoading, setIsReportLoading] = useState(false);
    const [reportError, setReportError] = useState('');

    // --- NEW: State for modals now living in this component ---
    const [isAddSimOpen, setIsAddSimOpen] = useState(false);
    const [isPdfUploadOpen, setIsPdfUploadOpen] = useState(false);

    // --- DATA FETCHING ---
    const fetchSims = async () => {
        try {
            const response = await api.get('/sims');
            setSims(response.data);
        } catch (error) {
            console.error("Error fetching SIMs:", error);
        }
    };

    useEffect(() => {
        fetchSims();
    }, []);

    // --- DATA PROCESSING & MEMOIZATION (No changes to logic) ---
    const organisationOptions = useMemo(() => {
        const orgs = new Set(sims.map(sim => sim.organisation).filter(Boolean));
        return Array.from(orgs).sort();
    }, [sims]);

    const processedSims = useMemo(() => {
        let filtered = [...sims];
        if (filters.organisation) {
            if (filters.organisation === '__UNASSIGNED__') {
                filtered = filtered.filter(sim => !sim.organisation);
            } else {
                filtered = filtered.filter(sim => sim.organisation === filters.organisation);
            }
        }
        if (filters.assignedTo) {
            const lowerUser = filters.assignedTo.toLowerCase();
            filtered = filtered.filter(sim => sim.device_user?.toLowerCase().includes(lowerUser));
        }
        if (quickSearchTerm) {
            const lowerTerm = quickSearchTerm.toLowerCase();
            filtered = filtered.filter(sim =>
                sim.sim_number?.toLowerCase().includes(lowerTerm) ||
                sim.mobile_number?.toLowerCase().includes(lowerTerm) ||
                sim.organisation?.toLowerCase().includes(lowerTerm) ||
                sim.device_user?.toLowerCase().includes(lowerTerm)
            );
        }
        return filtered;
    }, [sims, filters, quickSearchTerm]);

    const sortedSims = useMemo(() => {
        const sortable = [...processedSims];
        if (sortConfig.key) {
            sortable.sort((a, b) => {
                const aVal = a[sortConfig.key];
                const bVal = b[sortConfig.key];
                if (aVal == null) return 1;
                if (bVal == null) return -1;
                let comparison = 0;
                if (sortConfig.key === 'latest_usage_date') {
                    comparison = new Date(aVal) - new Date(bVal);
                } else if (sortConfig.key === 'latest_usage') {
                    comparison = parseFloat(aVal) - parseFloat(bVal);
                } else {
                    comparison = String(aVal).localeCompare(String(bVal));
                }
                return sortConfig.direction === 'asc' ? comparison : -comparison;
            });
        }
        return sortable;
    }, [processedSims, sortConfig]);

    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = sortedSims.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(sortedSims.length / recordsPerPage);

    // --- HANDLER FUNCTIONS (No changes to logic) ---
    const handleGenerateReport = async () => {
        setIsReportLoading(true);
        setReportError('');
        const url = filters.organisation
            ? `/reports/export-report?organisation=${encodeURIComponent(filters.organisation)}`
            : '/reports/export-report';
        try {
            const response = await api.get(url, { responseType: 'blob' });
            const disposition = response.headers['content-disposition'];
            let filename = 'report.zip';
            if (disposition) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            }
            const contentType = response.headers['content-type'] || 'application/octet-stream';
            const blob = new Blob([response.data], { type: contentType });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            if (err.response && err.response.status === 404) {
                 const reader = new FileReader();
                 reader.onload = () => {
                    try {
                        const errorData = JSON.parse(reader.result);
                        setReportError(errorData.error || 'No data available for this report.');
                    } catch (e) {
                        setReportError('No data available for this report.');
                    }
                 }
                 reader.readAsText(err.response.data);
            } else {
                setReportError('Failed to generate report. Please try again.');
            }
            console.error('Report generation error:', err);
        } finally {
            setIsReportLoading(false);
        }
    };
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };
    const handleQuickSearchChange = (e) => {
        setQuickSearchTerm(e.target.value);
        setCurrentPage(1);
    };
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };
    const handleRecordsPerPageChange = (e) => {
        setRecordsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedSims(currentRecords.map(sim => sim.id));
        } else {
            setSelectedSims([]);
        }
    };
    const handleSelectRow = (e, id) => {
        e.stopPropagation();
        setSelectedSims(prev =>
            prev.includes(id) ? prev.filter(simId => simId !== id) : [...prev, id]
        );
    };
    const handleBulkAssignUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await api.post('/sims/bulk-assign', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                responseType: 'blob',
            });
            if (response.headers['content-type'].includes('sheet')) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'error_report.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
                alert('Bulk assignment completed with some errors. An error report has been downloaded.');
            } else {
                const reader = new FileReader();
                reader.onload = function() {
                    const jsonResponse = JSON.parse(reader.result);
                    alert(jsonResponse.message || 'Bulk assignment successful!');
                };
                reader.readAsText(response.data);
            }
            fetchSims();
        } catch (error) {
            console.error('Error uploading bulk assignment:', error);
            alert('Failed to upload bulk assignment file. Check console for details.');
        }
        event.target.value = null;
    };
    const handleDeleteSingle = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm("Are you sure you want to delete this SIM?")) return;
        try {
            await api.delete(`/sims/${id}`);
            setSelectedSims(prev => prev.filter(simId => simId !== id));
            fetchSims();
        } catch (error) {
            console.error("Error deleting SIM:", error);
            alert("Failed to delete SIM.");
        }
    };
    const handleBulkDelete = async () => {
        if (!window.confirm(`Are you sure you want to delete ${selectedSims.length} selected SIM(s)?`)) return;
        try {
            await api.post('/sims/bulk-delete', { ids: selectedSims });
            setSelectedSims([]);
            fetchSims();
        } catch (error) {
            console.error("Error bulk deleting SIMs:", error);
            alert("Failed to delete selected SIMs.");
        }
    };
    const handleRowClick = async (simId) => {
        if (expandedSimId === simId) {
            setExpandedSimId(null);
            return;
        }
        setExpandedSimId(simId);
        setHistoryView('column');
        try {
            const response = await api.get(`/sims/${simId}/usage`);
            setUsageHistory(response.data);
        } catch (error) {
            console.error("Error fetching usage history:", error);
        }
    };
    const openAssignmentModal = (e, sim) => {
        e.stopPropagation();
        setSelectedSim(sim);
        setAssignmentData({
            organisation: sim.organisation || '',
            device_serial: sim.device_serial || '',
            device_user: sim.device_user || '',
            user_email: sim.user_email || ''
        });
        setIsAssignmentModalOpen(true);
    };
    const closeAssignmentModal = () => {
        setIsAssignmentModalOpen(false);
        setSelectedSim(null);
    };
    const handleAssignmentSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.put(`/sims/${selectedSim.id}/assign`, assignmentData);
            closeAssignmentModal();
            fetchSims();
        } catch (error) {
            console.error("Error assigning device:", error);
            alert("Failed to save assignment.");
        }
    };
    const getSortArrow = (key) => {
        if (sortConfig.key !== key) return '↕';
        if (sortConfig.direction === 'asc') return '↑';
        return '↓';
    };
    const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    };
    const chartData = useMemo(() => [...usageHistory].reverse().map(usage => ({
        month: formatDate(usage.usage_date),
        usage: parseFloat(usage.usage_mb)
    })), [usageHistory]);

    // --- JSX (RENDER) ---
    return (
        <div className="card">
            {/* --- ADJUSTED: Header now contains all SIM-specific actions --- */}
            <div className="component-header">
                <div>
                    <h3>Registered SIM Cards</h3>
                    <p>View, assign, and manage all SIM cards in the system.</p>
                </div>
                {/* CHANGE THIS LINE: from "header-actions" to "component-actions" */}
                <div className="component-actions">
                    <button className="btn btn-primary" onClick={() => setIsAddSimOpen(true)}>+ Add SIM</button>
                    <button className="btn btn-secondary" onClick={() => setIsPdfUploadOpen(true)}>📤 Upload Usage PDF</button>
                    <AllReportsButton />
                </div>
            </div>


            {/* --- ADJUSTED: Filter controls with consistent styling --- */}
            <div className="filter-controls">
                <div className="filter-group">
                    <label className="form-label">Filter by Organisation</label>
                    <select name="organisation" className="form-control" value={filters.organisation} onChange={handleFilterChange}>
                        <option value="">All Organisations</option>
                        <option value="__UNASSIGNED__">Unassigned SIMs</option>
                        {organisationOptions.map(org => (<option key={org} value={org}>{org}</option>))}
                    </select>
                </div>
                <div className="filter-group">
                    <label className="form-label">Filter by Assigned User</label>
                    <input type="text" name="assignedTo" className="form-control" placeholder="e.g. John Doe" value={filters.assignedTo} onChange={handleFilterChange} />
                </div>
                <div className="filter-group">
                    <label className="form-label">Quick Search</label>
                    <input type="text" className="form-control" placeholder="Search SIM#, Mobile#, Org, User..." value={quickSearchTerm} onChange={handleQuickSearchChange} />
                </div>
            </div>

            {/* --- ADJUSTED: Bulk actions and refresh button --- */}
            <div className="table-toolbar">
                <div className="bulk-actions">
                    {selectedSims.length > 0 && (
                        <button onClick={handleBulkDelete} className="btn btn-delete">
                            Delete Selected ({selectedSims.length})
                        </button>
                    )}
                    <label className="btn btn-secondary btn-file-input">
                        Bulk Assign (XLSX)
                        <input type="file" accept=".xlsx" onChange={handleBulkAssignUpload} />
                    </label>
                </div>
                <div className="toolbar-right-actions">
                    <div className="report-action-group">
                        <button onClick={handleGenerateReport} disabled={isReportLoading} className="btn btn-accent">
                            {isReportLoading ? 'Generating...' : 'Generate Filtered Report'}
                        </button>
                        {reportError && <p className="report-error">{reportError}</p>}
                    </div>
                    <button onClick={fetchSims} className="btn btn-cancel">Refresh List</button>
                </div>
            </div>

            {/* --- ADJUSTED: Table with consistent styling --- */}
            <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                    <thead>
                        <tr>
                            <th style={{ width: '40px' }}>
                                <input type="checkbox" onChange={handleSelectAll} checked={currentRecords.length > 0 && selectedSims.length === currentRecords.length} disabled={currentRecords.length === 0} />
                            </th>
                            <th>SIM Number</th>
                            <th>Mobile Number</th>
                            <th className="sortable-header" onClick={() => requestSort('organisation')}>Organisation {getSortArrow('organisation')}</th>
                            <th>Assigned To</th>
                            <th className="sortable-header" onClick={() => requestSort('latest_usage')}>Latest Usage (MB) {getSortArrow('latest_usage')}</th>
                            <th className="sortable-header" onClick={() => requestSort('latest_usage_date')}>Record Date {getSortArrow('latest_usage_date')}</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentRecords.map(sim => {
                            const isAssigned = sim.device_user || sim.device_serial;
                            const isSelected = selectedSims.includes(sim.id);
                            return (
                                <React.Fragment key={sim.id}>
                                    <tr className={`sim-row ${isSelected ? 'selected-row' : ''}`} onClick={() => handleRowClick(sim.id)}>
                                        <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={isSelected} onChange={(e) => handleSelectRow(e, sim.id)} /></td>
                                        <td>{sim.sim_number}</td>
                                        <td>{sim.mobile_number}</td>
                                        <td>{sim.organisation || <span className="italic-placeholder">Not Set</span>}</td>
                                        <td>
                                            {isAssigned ? (
                                                <div><strong>{sim.device_user || 'Unknown User'}</strong><br /><small>{sim.device_serial}</small></div>
                                            ) : (<span className="italic-placeholder">Unassigned</span>)}
                                        </td>
                                        <td>{sim.latest_usage ? `${sim.latest_usage} MB` : 'No Data'}</td>
                                        <td>{sim.latest_usage_date ? formatDate(sim.latest_usage_date) : <span className="italic-placeholder">N/A</span>}</td>
                                        <td className="table-actions">
                                            <button className={`btn ${isAssigned ? "btn-edit" : "btn-accent"}`} onClick={(e) => openAssignmentModal(e, sim)}>{isAssigned ? "Details" : "Assign"}</button>
                                            <button className="btn btn-delete" onClick={(e) => handleDeleteSingle(e, sim.id)}>Delete</button>
                                        </td>
                                    </tr>
                                    {expandedSimId === sim.id && (
                                        <tr className="expanded-row-bg">
                                            <td colSpan="8">
                                                <div className="expanded-content">
                                                    <div className="expanded-header">
                                                        <strong>Monthly Usage History for {sim.sim_number}</strong>
                                                        <div className="view-toggle-group">
                                                            <button className={`view-toggle-btn ${historyView === 'column' ? 'active' : ''}`} onClick={() => setHistoryView('column')}>Column View</button>
                                                            <button className={`view-toggle-btn ${historyView === 'graph' ? 'active' : ''}`} onClick={() => setHistoryView('graph')}>Graph View</button>
                                                        </div>
                                                    </div>
                                                    {usageHistory.length > 0 ? (
                                                        historyView === 'column' ? (
                                                            <table className="history-table">
                                                                <thead><tr><th>Month</th><th>Usage (MB)</th></tr></thead>
                                                                <tbody>{usageHistory.map(usage => (<tr key={usage.id}><td>{formatDate(usage.usage_date)}</td><td>{usage.usage_mb} MB</td></tr>))}</tbody>
                                                            </table>
                                                        ) : (
                                                            <div className="chart-container">
                                                                <ResponsiveContainer width="100%" height="100%"><LineChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="month" stroke="#8884d8" /><YAxis stroke="#8884d8" /><Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }} /><Line type="monotone" dataKey="usage" stroke="#92C04F" strokeWidth={3} activeDot={{ r: 6 }} /></LineChart></ResponsiveContainer>
                                                            </div>
                                                        )
                                                    ) : (<p className="no-data-message">No usage history recorded yet.</p>)}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {currentRecords.length === 0 && (
                            <tr><td colSpan="8" className="no-data-message">No SIM cards match the current filters.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* --- ADJUSTED: Pagination with consistent styling --- */}
            <div className="pagination-footer">
                <div className="records-per-page">
                    <label className="form-label" htmlFor="recordsPerPage">Rows per page:</label>
                    <select id="recordsPerPage" className="form-control" value={recordsPerPage} onChange={handleRecordsPerPageChange}>
                        <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
                    </select>
                </div>
                <div className="page-info">
                    <span>Page {currentPage} of {totalPages > 0 ? totalPages : 1}</span>
                    <span>(Total: {sortedSims.length} records)</span>
                </div>
                <div className="page-turner">
                    <button className="btn btn-secondary" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>Previous</button>
                    <button className="btn btn-secondary" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages || sortedSims.length === 0}>Next</button>
                </div>
            </div>

            {/* --- ADJUSTED: Assignment Modal with consistent styling --- */}
            {isAssignmentModalOpen && (
                <div className="modal-overlay" onClick={closeAssignmentModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{(selectedSim?.device_user || selectedSim?.device_serial) ? `Device Details: ${selectedSim?.sim_number}` : `Assign Device to ${selectedSim?.sim_number}`}</h2>
                            <button className="modal-close-btn" onClick={closeAssignmentModal}>✖</button>
                        </div>
                        <form onSubmit={handleAssignmentSubmit}>
                            <div className="modal-body">
                                <div className="form-group"><label className="form-label">Organisation</label><input type="text" className="form-control" value={assignmentData.organisation} onChange={(e) => setAssignmentData({...assignmentData, organisation: e.target.value})} placeholder="e.g. Main Corp" /></div>
                                <div className="form-group"><label className="form-label">Device Serial Number</label><input type="text" className="form-control" value={assignmentData.device_serial} onChange={(e) => setAssignmentData({...assignmentData, device_serial: e.target.value})} placeholder="e.g. SN-123456" /></div>
                                <div className="form-group"><label className="form-label">Device User</label><input type="text" className="form-control" value={assignmentData.device_user} onChange={(e) => setAssignmentData({...assignmentData, device_user: e.target.value})} placeholder="e.g. John Doe" /></div>
                                <div className="form-group"><label className="form-label">User Email</label><input type="email" className="form-control" value={assignmentData.user_email} onChange={(e) => setAssignmentData({...assignmentData, user_email: e.target.value})} placeholder="e.g. john@company.com" /></div>
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-cancel" onClick={closeAssignmentModal}>Cancel</button>
                                <button type="submit" className="btn btn-primary">Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- NEW: Modals for Add SIM and Upload PDF --- */}
            {isAddSimOpen && (
                <div className="modal-overlay" onClick={() => setIsAddSimOpen(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close-btn" onClick={() => setIsAddSimOpen(false)}>✖</button>
                        <AddSim onClose={() => { setIsAddSimOpen(false); fetchSims(); }} />
                    </div>
                </div>
            )}
            {isPdfUploadOpen && (
                <div className="modal-overlay" onClick={() => setIsPdfUploadOpen(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close-btn" onClick={() => setIsPdfUploadOpen(false)}>✖</button>
                        <PdfUpload onClose={() => { setIsPdfUploadOpen(false); fetchSims(); }} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SimList;
